import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { defaultButtonClasses, secondaryButtonClasses } from "@/constants/ui";
import { Calendar, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMakeABookingDialog } from "../useMakeABookingDialog";
import { formatDateForDisplay, formatDuration, formatTimeForDisplay } from "../utils";

export const ConfirmationScreen = () => {
  const navigate = useNavigate();
  const { createdBooking, formData, isElevatedUser, onClose } = useMakeABookingDialog();

  const handleViewBooking = () => {
    if (!createdBooking?.id) return;

    const path = isElevatedUser
      ? `/location/${formData.locationSlug}/practice-bookings/${createdBooking.id}`
      : `/view-booking/${createdBooking.id}`;

    navigate(path);
    onClose?.();
  };

  const handleViewOnCalendar = () => {
    if (!createdBooking?.date || !formData.locationSlug) return;

    // Format date as YYYY-MM-DD for URL
    const dateStr = format(new Date(createdBooking.date), "yyyy-MM-dd");

    navigate(`/location/${formData.locationSlug}/calendar?date=${dateStr}`);
    onClose?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  // For slot bookings the calendar-clicked date is NOT the booking's start date: a slot's first
  // occurrence is placed at least one calendar month out (R24), so `formData.date` (the clicked day)
  // would read as "today" and alarm PMs. `createdBooking.date` is the authoritative persisted start,
  // so use it for slots. Non-slot bookings keep the clicked date (identical, and avoids any TZ shift).
  const displayDate =
    formData.bookingType === "slot" && createdBooking?.date ? new Date(createdBooking.date) : formData.date;

  // Determine if this is a recurring booking
  const isRecurring = formData.isRecurring || formData.bookingType === "slot";
  const recurrenceLabel =
    formData.bookingType === "slot" ? "Slot membership" : formData.recurrencePattern?.label || "Weekly";

  const renderCTAs = () => (
    <div className="flex flex-col gap-3 w-full max-w-md">
      <Button className={`w-full ${defaultButtonClasses}`} onClick={handleViewBooking}>
        View Booking
      </Button>
      <Button variant="outline" className={`w-full ${secondaryButtonClasses}`} onClick={handleViewOnCalendar}>
        <Calendar className="mr-2 h-4 w-4" />
        View on Calendar
      </Button>
      <Button variant="outline" className={`w-full ${secondaryButtonClasses}`} onClick={handleClose}>
        Done
      </Button>
    </div>
  );

  return (
    <div className="w-full flex flex-col bg-white rounded-xl my-4">
      {/* Mobile view */}
      <div className="lg:hidden flex flex-col h-full">
        {/* Mobile Header */}
        <div className="p-4 py-6 flex flex-col items-center circle-pattern-background circle-pattern-indigo-50 rounded-t-2xl">
          <CheckCircle2 className="w-16 h-16 text-green-600 mb-4" />
          <h2 className="text-2xl font-semibold text-green-900 mb-2">Booking Confirmed!</h2>
          {createdBooking?.shortCode && (
            <p className="text-gray-600">
              Reference: <span className="font-mono font-semibold uppercase">{createdBooking.shortCode}</span>
            </p>
          )}
        </div>

        {/* Mobile Content */}
        <div className="flex-1 bg-gray-50 p-6 flex flex-col">
          {/* Booking Details Summary */}
          <div className="bg-white rounded-lg p-4 mb-6 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Booking Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Date</span>
                <span className="font-medium">{formatDateForDisplay(displayDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Time</span>
                <span className="font-medium">{formatTimeForDisplay(formData.time)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Duration</span>
                <span className="font-medium">{formatDuration(formData.durationMinutes)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Room</span>
                <span className="font-medium">{formData.roomName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Location</span>
                <span className="font-medium">{formData.locationName}</span>
              </div>
              {isRecurring && (
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-gray-500">Repeat</span>
                  <span className="font-medium text-indigo-600">{recurrenceLabel}</span>
                </div>
              )}
            </div>
          </div>

          {/* Mobile CTAs */}
          <div className="mt-auto">{renderCTAs()}</div>
        </div>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1">
        {/* Left column - Success message */}
        <div className="p-6 lg:rounded-lg flex flex-col items-center justify-center circle-pattern-background circle-pattern-indigo-50">
          <CheckCircle2 className="w-20 h-20 text-green-600 mb-6" />
          <h2 className="text-3xl font-semibold text-green-900 mb-3 text-center">Booking Confirmed!</h2>
          {createdBooking?.shortCode && (
            <p className="text-gray-600 text-center text-lg mb-2">
              Reference: <span className="font-mono font-semibold text-xl uppercase">{createdBooking.shortCode}</span>
            </p>
          )}
          <p className="text-gray-500 text-center text-sm mt-2">
            {formData.memberName !== formData.memberName
              ? "The member will be notified about this booking."
              : "Your booking has been successfully created."}
          </p>
        </div>

        {/* Right column - Details and CTAs */}
        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm flex flex-col">
          {/* Booking Details Summary */}
          <div className="bg-white rounded-lg p-6 mb-6 border border-gray-200">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Booking Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 block">Date</span>
                <span className="font-medium text-base">{formatDateForDisplay(displayDate)}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Time</span>
                <span className="font-medium text-base">{formatTimeForDisplay(formData.time)}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Duration</span>
                <span className="font-medium text-base">{formatDuration(formData.durationMinutes)}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Room</span>
                <span className="font-medium text-base">{formData.roomName}</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500 block">Location</span>
                <span className="font-medium text-base">{formData.locationName}</span>
              </div>
              {isRecurring && (
                <div className="col-span-2 pt-3 border-t">
                  <span className="text-gray-500 block">Repeat Pattern</span>
                  <span className="font-medium text-base text-indigo-600">{recurrenceLabel}</span>
                </div>
              )}
            </div>
          </div>

          {/* Desktop CTAs */}
          <div className="mt-auto flex flex-col items-center">{renderCTAs()}</div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationScreen;
